import React, { lazy, Suspense, useContext, useEffect, useState } from 'react';

import { NonCommerceInfoContext } from '@rsc/contexts/NonCommerceInfoContext';
import { ProductContainerContext } from '@rsc/contexts/ProductContainerContext';

import BazaarVoiceContainer from '@rsc/ui-components/tailwind/BazaarVoiceContainer';
import ConvertAB from '@rsc/ui-components/tailwind/ConvertAB';
import ProductImageCarousel from '@rsc/ui-components/tailwind/ProductImageCarousel';
import ProductSidebar from '@rsc/ui-components/tailwind/ProductSidebar';
import ProductDescription from '@rsc/ui-components/tailwind/ProductDescription';
import Breadcrumb from '@rsc/ui-components/dept/molecules/Breadcrumb';

import { getLink, pushToDataLayer } from '@rsc/utils';
import { SanitySiteSettings } from '@rsc/models/gatsby-graphql-types';
import { isPixelSupported, pixelCategoryFromLocation, PIXELS } from '@rsc/utils/pixels.ts';

interface Props {
  data: {
    product: ProductData;
    siteSettings: SanitySiteSettings;
  };
  location: any;
  pageContext: any;
}

const Blocks = lazy(
  () => import('@rsc/ui-components/dept/organisms/Blocks/Blocks')
);

export default function ProductContainer({
  data,
  location,
  pageContext,
}: Readonly<Props>) {
  const { isNonCommerce } = useContext(NonCommerceInfoContext) as any;
  const { product, activeVariant } = useContext(ProductContainerContext) as any;
  const [lookedAtSkus, setLookedAtSkus] = useState([]);

  const searchParams = new URLSearchParams(location?.search);
  const collectionLocation = decodeURIComponent(
    searchParams.get('collection-location') || ''
  );

  const getBreadcrumbs = () => {
    let breadcrumbs = [{ pathname: '/shop', title: 'Shop' }];
    breadcrumbs.push({
      title: product?.shopifyProduct?.title,
      pathname: '',
    });

    if (data?.product?.breadcrumbs?.length > 0) {
      breadcrumbs = [
        ...data.product.breadcrumbs.map(pn => ({
          pathname: getLink(pn.linkType, pn.href, pn.internalLink),
          title: pn.caption,
        })),
        {
          title: product?.shopifyProduct?.title,
        },
      ];
    } else if (collectionLocation) {
      const cleanCollectionLocation = collectionLocation
        ? collectionLocation.substring(collectionLocation.indexOf('/shop'))
        : '';

      const collectionLocationParams =
        cleanCollectionLocation
          ?.split('/')
          .filter(p => p.startsWith('?'))?.[0] || '';

      breadcrumbs = [
        // Use the url search query for this location
        ...cleanCollectionLocation
          .split('/') // Split it on each sub category (including shop all)
          .slice(1) // Remove the leading slash "/"
          .filter(bc => bc?.length) // Remove any junk that snuck in
          .filter(d => !d.startsWith('?')) // Remove search query params
          // Use the pathname
          // And do a reverse slugify on the collection title name
          .map((path, i, r) => ({
            pathname: `${path}${
              r?.length === i + 1 ? collectionLocationParams : ''
            }`,
            title: path
              .replace(/\band\b/g, '&')
              .split('-')
              .map(s => s.charAt(0).toUpperCase() + s.substring(1))
              .join(' '),
          })),
        {
          title: product?.shopifyProduct?.title,
          pathname: '',
        },
      ];
    }

    if (breadcrumbs?.length === 1 && breadcrumbs[0]?.title !== 'Shop') {
      breadcrumbs = [{ pathname: '/shop', title: 'Shop' }, ...breadcrumbs];
    }

    return breadcrumbs;
  };

  useEffect(() => {
    (async () => {
      if (activeVariant && !lookedAtSkus.includes(activeVariant.sku)) {
        setLookedAtSkus([...lookedAtSkus, activeVariant.sku]);
        pushToDataLayer('view_item', {
          ecommerce: {
            currency: 'USD',
            value: activeVariant.price,
          },
          items: [
            {
              variant: activeVariant,
              product: product?.shopifyProduct,
              category: collectionLocation,
            },
          ],
        });

        const collectionLocationMapping = collectionLocation
          ?.split('/')
          .filter(n => n)
          .slice(0, 5);

        if (isPixelSupported(PIXELS.klaviyo)) {
          const klaviyoItem = {
            ProductName: product?.shopifyProduct?.title,
            shopifyId: product?.shopifyProduct?.shopifyId,
            SKU: activeVariant?.sku,
            Categories: collectionLocationMapping,
            ImageURL: activeVariant?.media[0]?.image?.src,
            Brand: product?.shopifyProduct?.vendor,
            Price: activeVariant?.price,
            URL: window.location.href,
          };
          // @ts-expect-error - Klaviyo is not typed
          window?.klaviyo?.push(['track', 'Viewed Product', klaviyoItem]);
        }

        if (isPixelSupported(PIXELS.adroll)) {
          // @ts-expect-error - Adroll is not typed
          window?.adroll?.track('productView', {
            products: [
              {
                product_id: activeVariant?.sku,
                price: activeVariant?.price,
                category: pixelCategoryFromLocation(location),
              },
            ],
          });
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeVariant]);

  return (
    <>
      <ConvertAB
        pageType={isNonCommerce ? 'noncommerce-pdp' : 'pdp'}
        productName={product?.shopifyProduct?.title}
        sku={activeVariant?.sku}
        price={activeVariant?.price}
        categoryName={collectionLocation}
      />
      <div className="mx-auto flex justify-center items-start shrink-0 px-4 md:px-10 lg:px-20 2xl:px-28 max-w-[1680px]">
        <div className="grid grid-cols-6 lg:grid-cols-12 gap-6">
          <div className="col-span-6 lg:col-span-8">
            <div className="block mb-10 lg:mb-20">
              <Breadcrumb
                breadcrumbs={getBreadcrumbs()}
                custom={data?.product?.breadcrumbs?.length > 0}
              />
            </div>
            <ProductImageCarousel />
            <div id="mobile-sidebar" className="lg:hidden">
              <ProductSidebar siteSettings={data?.siteSettings} />
            </div>
            <h2 className="mt-12 lg:mt-0">{product?.descriptionHeading}</h2>
            <ProductDescription
              description={product?.shopifyProduct?.description}
            />
            <Suspense fallback={null}>
              {product?.productBlocks?.blocks?.length && (
                <Blocks
                  blocks={product?.productBlocks?.blocks}
                  data={data}
                  pageContext={pageContext}
                  siteSettings={data?.siteSettings}
                  manuals={product?.manuals}
                  isPDP
                  location={location}
                />
              )}
            </Suspense>
            <div
              id="bazaarVoice"
              className="w-full bg-background relative z-10"
            >
              <BazaarVoiceContainer
                sku={product?.shopifyProduct?.variants?.[0]?.sku}
              />
            </div>
          </div>
          <div className="hidden lg:block lg:col-span-4 pl-6 lg:border-l border-border">
            <div
              id="desktop-sidebar"
              className="overflow-y-auto sticky top-[112px] max-h-[calc(100vh-112px)]"
            >
              <ProductSidebar
                siteSettings={data?.siteSettings}
                buttonFull={false}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
